/**
 * MediaLibraryForm - Polished search input with sub-filters
 * Canva/Wix-inspired design with rounded search bar and pill filters
 */

import { Search, Loader2 } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MediaType,
  MediaProvider,
  MediaOrientation,
  MediaColor,
  MediaSortOrder,
  VideoSize,
  VideoType,
  MEDIA_PROVIDERS,
  ORIENTATION_OPTIONS,
  COLOR_OPTIONS,
  SORT_OPTIONS,
  VIDEO_SIZE_OPTIONS,
  VIDEO_TYPE_OPTIONS,
} from "@/types/media-library-panel.types";
import { cn } from "@/lib/utils";
import { useFeatures } from "@/hooks/useFeatures";

interface MediaLibraryFormProps {
  query: string;
  onQueryChange: (value: string) => void;
  mediaType: MediaType;
  provider: MediaProvider | "all";
  onProviderChange: (value: MediaProvider | "all") => void;
  orientation: MediaOrientation | "any";
  onOrientationChange: (value: MediaOrientation | "any") => void;
  color: MediaColor | "any";
  onColorChange: (value: MediaColor | "any") => void;
  sortOrder: MediaSortOrder;
  onSortOrderChange: (value: MediaSortOrder) => void;
  videoSize: VideoSize | "any";
  onVideoSizeChange: (value: VideoSize | "any") => void;
  videoType: VideoType;
  onVideoTypeChange: (value: VideoType) => void;
  isSearching: boolean;
  onSearch: () => void;
  isMobile?: boolean;
}

export function MediaLibraryForm({
  query,
  onQueryChange,
  mediaType,
  provider,
  onProviderChange,
  orientation,
  onOrientationChange,
  color,
  onColorChange,
  sortOrder,
  onSortOrderChange,
  videoSize,
  onVideoSizeChange,
  videoType,
  onVideoTypeChange,
  isSearching,
  onSearch,
  isMobile = false,
}: MediaLibraryFormProps) {
  const { features } = useFeatures();
  const enabledProviderNames = features.mediaLibraryProviders ?? ['pexels', 'unsplash', 'freesound'];

  const availableProviders = MEDIA_PROVIDERS.filter((p) =>
    p.supportedTypes.includes(mediaType) &&
    (p.value === "all" || enabledProviderNames.includes(p.value)),
  );

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" && !isSearching) {
      e.preventDefault();
      onSearch();
    }
  };

  return (
    <div className={cn(isMobile ? "space-y-2" : "space-y-2.5")}>
      {/* Search bar - rounded with icon */}
      <div className="relative group">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isSearching ? (
            <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-muted-foreground group-focus-within:text-foreground transition-colors" />
          )}
        </div>
        <input
          type="text"
          placeholder="Search images, videos, sounds..."
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full rounded-full bg-muted/60 border-0",
            "pl-10 pr-4",
            "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-background",
            "placeholder:text-muted-foreground/60",
            "transition-all duration-200",
            isMobile ? "h-9 text-[13px]" : "h-10 text-sm",
          )}
        />
      </div>

      {/* Sub-filters: pill-shaped selects */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Select value={provider} onValueChange={onProviderChange}>
          <SelectTrigger
            className={cn(
              "w-fit rounded-full border border-border/60 bg-background font-medium",
              "hover:bg-muted/50 hover:border-border transition-all",
              "flex-shrink-0 focus:ring-0 focus:ring-offset-0 shadow-none",
              "data-[state=open]:bg-muted/50 data-[state=open]:border-primary/30",
              isMobile ? "h-7 px-2.5 text-[11px] gap-1" : "h-7 px-3 text-xs gap-1.5",
            )}
          >
            <SelectValue placeholder="Provider" />
          </SelectTrigger>
          <SelectContent>
            {availableProviders.map((p) => (
              <SelectItem key={p.value} value={p.value} className="text-sm">
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(mediaType === "image" || (mediaType === "video" && provider !== "pixabay")) && (
          <Select value={orientation} onValueChange={onOrientationChange}>
            <SelectTrigger
              className={cn(
                "w-fit rounded-full border border-border/60 bg-background font-medium",
                "hover:bg-muted/50 hover:border-border transition-all",
                "flex-shrink-0 focus:ring-0 focus:ring-offset-0 shadow-none",
                "data-[state=open]:bg-muted/50 data-[state=open]:border-primary/30",
                isMobile ? "h-7 px-2.5 text-[11px] gap-1" : "h-7 px-3 text-xs gap-1.5",
              )}
            >
              <SelectValue placeholder="Orientation" />
            </SelectTrigger>
            <SelectContent>
              {ORIENTATION_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-sm">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {mediaType === "image" && (
          <Select value={color} onValueChange={onColorChange}>
            <SelectTrigger
              className={cn(
                "w-fit rounded-full border border-border/60 bg-background font-medium",
                "hover:bg-muted/50 hover:border-border transition-all",
                "flex-shrink-0 focus:ring-0 focus:ring-offset-0 shadow-none",
                "data-[state=open]:bg-muted/50 data-[state=open]:border-primary/30",
                isMobile ? "h-7 px-2.5 text-[11px] gap-1" : "h-7 px-3 text-xs gap-1.5",
              )}
            >
              <SelectValue placeholder="Color" />
            </SelectTrigger>
            <SelectContent>
              {COLOR_OPTIONS.map((c) => (
                <SelectItem key={c.value} value={c.value} className="text-sm">
                  <span className="flex items-center gap-1.5">
                    {c.hex ? (
                      <span
                        className="inline-block w-3 h-3 rounded-full border border-border/40 flex-shrink-0"
                        style={{ backgroundColor: c.hex }}
                      />
                    ) : c.value === "black_and_white" ? (
                      <span className="inline-block w-3 h-3 rounded-full flex-shrink-0 overflow-hidden border border-border/40"
                        style={{
                          background: "linear-gradient(135deg, #171717 50%, #F5F5F5 50%)",
                        }}
                      />
                    ) : null}
                    {c.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {provider !== "pexels" && (
          <Select value={sortOrder} onValueChange={onSortOrderChange}>
            <SelectTrigger
              className={cn(
                "w-fit rounded-full border border-border/60 bg-background font-medium",
                "hover:bg-muted/50 hover:border-border transition-all",
                "flex-shrink-0 focus:ring-0 focus:ring-offset-0 shadow-none",
                "data-[state=open]:bg-muted/50 data-[state=open]:border-primary/30",
                isMobile ? "h-7 px-2.5 text-[11px] gap-1" : "h-7 px-3 text-xs gap-1.5",
              )}
            >
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value} className="text-sm">
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {mediaType === "video" && provider === "pexels" && (
          <Select value={videoSize} onValueChange={onVideoSizeChange}>
            <SelectTrigger
              className={cn(
                "w-fit rounded-full border border-border/60 bg-background font-medium",
                "hover:bg-muted/50 hover:border-border transition-all",
                "flex-shrink-0 focus:ring-0 focus:ring-offset-0 shadow-none",
                "data-[state=open]:bg-muted/50 data-[state=open]:border-primary/30",
                isMobile ? "h-7 px-2.5 text-[11px] gap-1" : "h-7 px-3 text-xs gap-1.5",
              )}
            >
              <SelectValue placeholder="Quality" />
            </SelectTrigger>
            <SelectContent>
              {VIDEO_SIZE_OPTIONS.map((v) => (
                <SelectItem key={v.value} value={v.value} className="text-sm">
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {mediaType === "video" && provider === "pixabay" && (
          <Select value={videoType} onValueChange={onVideoTypeChange}>
            <SelectTrigger
              className={cn(
                "w-fit rounded-full border border-border/60 bg-background font-medium",
                "hover:bg-muted/50 hover:border-border transition-all",
                "flex-shrink-0 focus:ring-0 focus:ring-offset-0 shadow-none",
                "data-[state=open]:bg-muted/50 data-[state=open]:border-primary/30",
                isMobile ? "h-7 px-2.5 text-[11px] gap-1" : "h-7 px-3 text-xs gap-1.5",
              )}
            >
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {VIDEO_TYPE_OPTIONS.map((v) => (
                <SelectItem key={v.value} value={v.value} className="text-sm">
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}
